import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building, 
  User, 
  Palette,
  ArrowRight,
  CheckCircle,
  Loader2,
  Check
} from 'lucide-react';
import { getEnabledThemes, applyTheme, getActiveTheme } from '../config/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 1, title: 'Business Info', icon: Building },
  { id: 2, title: 'Personal Details', icon: User },
  { id: 3, title: 'Choose Theme', icon: Palette },
  { id: 4, title: 'Get Started', icon: CheckCircle },
];

export function Onboarding() {
  const navigate = useNavigate();
  const { completeOnboarding, orgId, token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    tradetype: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  // Theme selection state
  const [selectedTheme, setSelectedTheme] = useState('commercial');
  const availableThemes = getEnabledThemes();

  // Apply theme preview on selection
  useEffect(() => {
    if (currentStep === 3) {
      applyTheme(selectedTheme);
    }
  }, [selectedTheme, currentStep]);

  // Initialize with current theme
  useEffect(() => {
    const currentTheme = getActiveTheme();
    setSelectedTheme(currentTheme.id);
  }, []);

  const handleNext = async () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete onboarding
      setLoading(true);
      
      try {
        // Update org name if provided
        if (formData.businessName && orgId) {
          await fetch(`${API_URL}/api/org/${orgId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ name: formData.businessName })
          });
        }
        
        // Save theme preference to user profile
        try {
          await fetch(`${API_URL}/api/auth/me/preferences`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ theme: selectedTheme })
          });
        } catch (err) {
          // Non-blocking - theme is already in localStorage
          console.warn('Could not save theme preference to profile:', err);
        }
        
        // Ensure theme is applied
        applyTheme(selectedTheme);
        
        // Mark onboarding as complete
        const result = await completeOnboarding();
        
        if (result.success) {
          navigate('/command-centre');
        } else {
          console.error('Failed to complete onboarding:', result.error);
          navigate('/command-centre');
        }
      } catch (err) {
        console.error('Onboarding error:', err);
        navigate('/command-centre');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSkipTheme = () => {
    // Use default theme
    setSelectedTheme('commercial');
    applyTheme('commercial');
    setCurrentStep(4);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    applyTheme(themeId);
  };

  return (
    <div className="auth-page">
      <div className={`auth-card ${currentStep === 3 ? 'auth-card-wide' : ''}`}>
        {/* Logo */}
        <div className="auth-logo">
          <h1>Trade<span>Life</span></h1>
          <p>Let's get you set up</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
          {steps.map((step, index) => (
            <div 
              key={step.id}
              className="flex items-center gap-sm"
            >
              <div 
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-full)',
                  background: currentStep >= step.id ? 'var(--accent-primary)' : 'var(--glass-bg)',
                  border: '1px solid',
                  borderColor: currentStep >= step.id ? 'var(--accent-primary)' : 'var(--glass-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: currentStep >= step.id ? 'white' : 'var(--text-muted)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
              >
                {currentStep > step.id ? <CheckCircle size={16} /> : step.id}
              </div>
              {index < steps.length - 1 && (
                <div 
                  style={{
                    width: '30px',
                    height: '2px',
                    background: currentStep > step.id ? 'var(--accent-primary)' : 'var(--glass-border)',
                    transition: 'all 0.2s ease'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Business Info */}
        {currentStep === 1 && (
          <div>
            <h2 className="auth-title">Business Information</h2>
            <p className="auth-subtitle">Tell us about your business</p>
            
            <div className="form-group">
              <label className="form-label">Business Name</label>
              <input
                type="text"
                name="businessName"
                className="form-input"
                placeholder="Your Company Ltd"
                value={formData.businessName}
                onChange={handleInputChange}
                data-testid="business-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Trade Type</label>
              <select 
                name="tradetype" 
                className="form-input"
                value={formData.tradetype}
                onChange={handleInputChange}
                data-testid="trade-type-select"
              >
                <option value="">Select your trade</option>
                <option value="plumber">Plumber</option>
                <option value="electrician">Electrician</option>
                <option value="carpenter">Carpenter</option>
                <option value="builder">Builder</option>
                <option value="roofer">Roofer</option>
                <option value="painter">Painter & Decorator</option>
                <option value="landscaper">Landscaper</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Personal Details */}
        {currentStep === 2 && (
          <div>
            <h2 className="auth-title">Personal Details</h2>
            <p className="auth-subtitle">We'll use this to set up your account</p>
            
            <div className="onboarding-form-grid">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  data-testid="first-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="john@company.com"
                value={formData.email}
                onChange={handleInputChange}
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder="07700 900000"
                value={formData.phone}
                onChange={handleInputChange}
                data-testid="phone-input"
              />
            </div>
          </div>
        )}

        {/* Step 3: Theme Selection */}
        {currentStep === 3 && (
          <div data-testid="theme-selection-step">
            <h2 className="auth-title">Choose your TradeLife theme</h2>
            <p className="auth-subtitle">You can change this anytime later — most people like to pick one now.</p>
            
            {/* Theme Grid */}
            <div className="onboarding-theme-grid">
              {availableThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleThemeSelect(theme.id)}
                  style={{
                    background: selectedTheme === theme.id 
                      ? 'var(--glass-bg-active)' 
                      : 'var(--glass-bg)',
                    border: '2px solid',
                    borderColor: selectedTheme === theme.id 
                      ? theme.colors.accent 
                      : 'var(--glass-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-md)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  data-testid={`theme-card-${theme.id}`}
                >
                  {/* Selected indicator */}
                  {selectedTheme === theme.id && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: 'var(--space-sm)',
                        right: 'var(--space-sm)',
                        width: '24px',
                        height: '24px',
                        borderRadius: 'var(--radius-full)',
                        background: theme.colors.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                    >
                      <Check size={14} />
                    </div>
                  )}
                  
                  {/* Color preview */}
                  <div 
                    style={{
                      display: 'flex',
                      gap: 'var(--space-xs)',
                      marginBottom: 'var(--space-md)'
                    }}
                  >
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-md)',
                        background: theme.preview.primary,
                        boxShadow: selectedTheme === theme.id 
                          ? `0 0 20px ${theme.colors.accentGlow}` 
                          : 'none',
                        transition: 'box-shadow 0.2s ease'
                      }}
                    />
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-md)',
                        background: theme.preview.secondary
                      }}
                    />
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-md)',
                        background: theme.preview.accent
                      }}
                    />
                  </div>
                  
                  {/* Theme info */}
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {theme.name}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                    {theme.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Ready */}
        {currentStep === 4 && (
          <div style={{ textAlign: 'center', padding: 'var(--space-lg) 0' }}>
            <div 
              style={{
                width: '64px',
                height: '64px',
                borderRadius: 'var(--radius-full)',
                background: 'var(--status-success-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto var(--space-lg)',
                color: 'var(--status-success)'
              }}
            >
              <CheckCircle size={32} />
            </div>
            <h2 className="auth-title">You're all set!</h2>
            <p className="auth-subtitle">
              Welcome to TradeLife. Let's take you to your Command Centre.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <button 
            className="btn btn-primary w-full"
            onClick={handleNext}
            disabled={loading}
            data-testid="onboarding-next-btn"
            style={{ padding: 'var(--space-md) var(--space-lg)' }}
          >
            {loading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Setting up...
              </>
            ) : (
              <>
                {currentStep === 4 ? 'Go to Command Centre' : currentStep === 3 ? 'Finish setup' : 'Continue'}
                <ArrowRight size={18} />
              </>
            )}
          </button>
          
          {/* Skip theme option */}
          {currentStep === 3 && (
            <button 
              className="btn btn-ghost w-full mt-md"
              onClick={handleSkipTheme}
              data-testid="skip-theme-btn"
              style={{ 
                padding: 'var(--space-sm) var(--space-lg)',
                color: 'var(--text-muted)',
                fontSize: '0.875rem'
              }}
            >
              Skip for now (use default)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
